// PasswordInput — Input shell with a trailing show/hide visibility toggle.
// Same wrapper classes as Input so it inherits the form.scss shell styles.
// Masked state shows the open eye (per design); visible state shows eye-off.

import { forwardRef, useState, type InputHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';

export interface PasswordInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Render the error (red) border. */
  error?: boolean;
}

const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(function PasswordInput(
  { error = false, className = '', ...rest },
  ref,
) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  const classes = ['app-input', error && 'app-input--error', className].filter(Boolean).join(' ');

  return (
    <span className={classes}>
      <input
        ref={ref}
        type={visible ? 'text' : 'password'}
        className='app-input__control'
        aria-invalid={error || undefined}
        {...rest}
      />
      <button
        type='button'
        className='app-input__toggle'
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? t('aria_hide_password') : t('aria_show_password')}
      >
        <Icon name={visible ? 'eye-off' : 'eye'} size={18} aria-hidden='true' />
      </button>
    </span>
  );
});

export default PasswordInput;
